import { useAsyncData } from '#imports'
import { computed } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import { toSaleCards } from './useSaleCards.helpers'

// Keep enough orderlines to fetch replacements alongside their originals,
// independently of the number of cards displayed in the section.
const ORDERLINES_PAGE_SIZE = 20

/**
 * Sale cards for the Help Center orders section: one card per orderline,
 * with replaced orderlines grouped with their replacement(s).
 */
export function useSaleCards() {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()
  const { isUserAuthenticated } = useUserStore()
  const resolveLocalizedRoute = useRouteLocationWithLocale()

  const { data: orderlines, status } = useAsyncData(
    () =>
      $httpFetch('/my-orders/orderlines', {
        query: { pageSize: ORDERLINES_PAGE_SIZE },
      }),
    { immediate: isUserAuthenticated.value },
  )

  const isPending = computed(() => status.value === 'pending')
  const hasSales = computed(
    () =>
      (orderlines.value?.count ?? 0) > 0 ||
      (orderlines.value?.results.length ?? 0) > 0,
  )
  const hasMoreSales = computed(
    () =>
      Boolean(orderlines.value?.next) ||
      (orderlines.value?.count ?? 0) > (orderlines.value?.results.length ?? 0),
  )

  const ordersPageTo = resolveLocalizedRoute({
    type: 'internal',
    name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.SELF,
    params: {},
    query: {},
  })

  // One card per orderline, replaced orderlines grouped with their
  // replacement(s), newest first. The overall display cap (sales +
  // trade-ins combined) is applied by the component, not here.
  const salesCards = computed(() =>
    toSaleCards(orderlines.value?.results, { i18n, ordersPageTo }),
  )

  return { salesCards, hasSales, hasMoreSales, isPending, status }
}
